import React, { FC, useState, useEffect, useCallback } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import useTurbinePath from '../../Shared/UseTurbinePathHook';
import axios from '../../../axios';
import { MatchTurbinePathParams } from '../../../shared/models/matchTurbinePathParams';
import Table from 'react-bootstrap/Table';
import { DeepzoomStatus } from '../../../shared/models/deepzoomStatus.enum';
import styles from './DeepzoomStatus.module.scss';
import DeepzoomCreationStatus from './DeepzoomCreationStatus';
import Button from '../../Shared/Button/Button';
import { ToastAnt } from '../../Shared/Toast/ToastAnt';

type TurbineDeepzoomInfo = {
  path: string;
  statuses: Array<DeepzoomStatusModel>;
};
type DeepzoomStatusModel = {
  bladeBladeSide: string;
  status: DeepzoomStatus;
  isStitchingFinished: boolean;
};

const TurbineDeepzoomStatus: FC<RouteComponentProps<MatchTurbinePathParams>> = ({ history, match }) => {
  const [path, setPath] = useState('');
  const [statuses, setStatuses] = useState<Array<DeepzoomStatusModel>>([]);
  const [site, inspection, turbine] = useTurbinePath(match.params);
  const [anyNotCompleted, setAnyNotCompleted] = useState(false);

  const getStatuses = useCallback(async () => {
    const url = `Info/deepzoomes/${site}/${inspection}/${turbine}`;
    try {
      let response = await axios.get<TurbineDeepzoomInfo>(url);

      setPath(response.data.path);
      setStatuses(response.data.statuses);
      setAnyNotCompleted(response.data.statuses.some(x => x.status === DeepzoomStatus.inProgress || x.status === DeepzoomStatus.notStarted));
    } catch (error) {}
  }, [inspection, site, turbine]);

  useEffect(() => {
    if (site && inspection && turbine) getStatuses();
  }, [site, inspection, turbine, getStatuses]);

  useEffect(() => {
    let id: any;

    if (anyNotCompleted) {
      id = setInterval(() => {
        getStatuses();
      }, 10000);
    }

    return () => {
      if (id) {
        clearInterval(id);
      }
    };
  }, [anyNotCompleted, getStatuses]);

  const startDeepzoomCreation = async (bladeBladeSide: string) => {
    const url = `info/deepzoomes/re-run/${site}/${inspection}/${turbine}/${bladeBladeSide.split('/')[0]}/${bladeBladeSide.split('/')[1]}`;

    let status = statuses.find(x => x.bladeBladeSide === bladeBladeSide);

    if (!status) {
      return;
    }

    try {
      status.status = DeepzoomStatus.inProgress;
      setAnyNotCompleted(true);
      setStatuses([...statuses]);

      await axios.post(url, null);

      ToastAnt('Starting...');
    } catch (error) {
      ToastAnt('An error occured...');
    }
  };

  let statusesList = statuses.map(x => {
    return (
      <tr key={x.bladeBladeSide} className={styles.statusRow}>
        <td className={styles.bladeValue}>
          <span>{x.bladeBladeSide.split('/')[0]}</span>
        </td>
        <td className={styles.bladeSideValue}>
          <span>{x.bladeBladeSide.split('/')[1]}</span>
        </td>
        <td>
          <DeepzoomCreationStatus status={x.status} />
        </td>
        <td className={styles.actions}>
          <Button
            title='Start'
            onClick={() => startDeepzoomCreation(x.bladeBladeSide)}
            disabled={x.status === DeepzoomStatus.inProgress || !x.isStitchingFinished}
            type='button'
          />
        </td>
      </tr>
    );
  });

  return (
    <div className={`${styles.deepzoomContainer} container`}>
      {site && inspection && turbine && (
        <div className={styles.path}>
          <div>
            <span className={styles.pathTitle}>Site: </span>
            <span>{path.split('/')[0]}</span>
          </div>
          <div>
            <span className={styles.pathTitle}>Inspection: </span>
            <span>{path.split('/')[1]}</span>
          </div>
          <div>
            <span className={styles.pathTitle}>Turbine: </span>
            <span>{path.split('/')[2]}</span>
          </div>
        </div>
      )}
      <Table striped bordered hover className={styles.deepzoomTable}>
        <thead>
          <tr>
            <th>Blade</th>
            <th>Blade Side</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {statuses.length > 0 ? (
            statusesList
          ) : (
            <tr>
              <td colSpan={4}>There is no data for this turbine.</td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default TurbineDeepzoomStatus;
